export const CATALOG_KIND_ADDON = "ADDON";
export const CATALOG_KIND_PREORDER = "PREORDER";

export const CATALOG_NAME_MAX = 200;
export const CATALOG_DESCRIPTION_MAX = 2000;
export const CATALOG_SORT_MAX = 9999;
export const CATALOG_EVENT_STOCK_MIN = 1;
export const CATALOG_EVENT_STOCK_MAX = 100000;

export function unwrapApiData(res) {
  if (res == null) return null;
  if (typeof res !== "object") return res;
  if (res.data !== undefined) return res.data;
  return res;
}

/** GET /event/eventDetails/:id → { message, data } where data is the event or [event]. */
export function unwrapEventRecord(res) {
  const data = unwrapApiData(res);
  if (Array.isArray(data)) {
    const first = data[0];
    return first && typeof first === "object" ? first : null;
  }
  return data && typeof data === "object" ? data : null;
}

export function catalogItemId(item) {
  if (item == null) return "";
  if (typeof item === "string" || typeof item === "number") return String(item);
  return String(item._id ?? item.id ?? item.itemId ?? "").trim();
}

export function normalizeCatalogItem(raw) {
  if (!raw || typeof raw !== "object") return null;
  const kind = String(raw.kind ?? "").trim().toUpperCase();
  const id = catalogItemId(raw);
  if (!id) return null;
  const unitPrice = Number(raw.unitPrice ?? raw.unit_price ?? 0);
  return {
    _id: id,
    clubId: String(raw.clubId ?? raw.clubid ?? "").trim(),
    kind: kind === CATALOG_KIND_PREORDER ? CATALOG_KIND_PREORDER : CATALOG_KIND_ADDON,
    name: String(raw.name ?? "").trim(),
    description: String(raw.description ?? "").trim(),
    image: String(raw.image ?? raw.imageUrl ?? ""),
    unitPrice: Number.isFinite(unitPrice) ? unitPrice : 0,
    currency: String(raw.currency ?? "INR").trim() || "INR",
    isactive: raw.isactive !== false,
    sortOrder: Number.isFinite(Number(raw.sortOrder ?? raw.sort_order))
      ? Number(raw.sortOrder ?? raw.sort_order)
      : 0,
    createdAt: raw.createdAt ?? raw.created_at ?? "",
    updatedAt: raw.updatedAt ?? raw.updated_at ?? "",
  };
}

export function unwrapCatalogList(res) {
  const data = unwrapApiData(res) ?? res;
  const root = data && typeof data === "object" ? data : {};
  const items = (Array.isArray(root.items) ? root.items : [])
    .map(normalizeCatalogItem)
    .filter(Boolean);
  const addons = (Array.isArray(root.addons) ? root.addons : items.filter((i) => i.kind === CATALOG_KIND_ADDON))
    .map(normalizeCatalogItem)
    .filter(Boolean);
  const preorders = (
    Array.isArray(root.preorders) ? root.preorders : items.filter((i) => i.kind === CATALOG_KIND_PREORDER)
  )
    .map(normalizeCatalogItem)
    .filter(Boolean);
  const merged = [...items];
  for (const row of [...addons, ...preorders]) {
    if (!merged.some((i) => i._id === row._id)) merged.push(row);
  }
  merged.sort((a, b) => (a.sortOrder - b.sortOrder) || a.name.localeCompare(b.name));
  return {
    items: merged,
    addons: merged.filter((i) => i.kind === CATALOG_KIND_ADDON),
    preorders: merged.filter((i) => i.kind === CATALOG_KIND_PREORDER),
  };
}

export function unwrapCatalogItem(res) {
  return normalizeCatalogItem(unwrapApiData(res) ?? res);
}

function readCatalogAttachQty(row) {
  if (!row || typeof row !== "object") return null;
  if (row.qty != null && row.qty !== "") return row.qty;
  if (row.stock != null && row.stock !== "") return row.stock;
  if (row.quantity != null && row.quantity !== "") return row.quantity;
  return null;
}

function normalizeEventCatalogSnapshotRow(row) {
  const itemId = catalogItemId(row);
  if (!itemId) return null;
  const qtyRaw = readCatalogAttachQty(row);
  const qtyNum = qtyRaw == null || qtyRaw === "" ? NaN : Number(qtyRaw);
  const hasQty = Number.isFinite(qtyNum);
  const qtySoldNum = Number(row?.qtySold ?? row?.sold ?? 0);
  const qtySold = Number.isFinite(qtySoldNum) ? qtySoldNum : 0;
  const remainingNum = Number(row?.remaining);
  return {
    itemId,
    name: String(row?.name ?? "").trim(),
    description: String(row?.description ?? "").trim(),
    image: String(row?.image ?? ""),
    unitPrice: Number(row?.unitPrice ?? 0) || 0,
    currency: String(row?.currency ?? "INR") || "INR",
    qty: hasQty ? qtyNum : null,
    qtySold,
    remaining: hasQty
      ? Number.isFinite(remainingNum)
        ? remainingNum
        : Math.max(0, qtyNum - qtySold)
      : null,
    soldOut: Boolean(row?.soldOut),
    available: row?.available,
    unlimited: !hasQty,
  };
}

export function eventCatalogSnapshots(event) {
  const addons = Array.isArray(event?.catalogAddons) ? event.catalogAddons : [];
  const preorders = Array.isArray(event?.catalogPreorders)
    ? event.catalogPreorders
    : [];
  return {
    addons: addons.map(normalizeEventCatalogSnapshotRow).filter(Boolean),
    preorders: preorders.map(normalizeEventCatalogSnapshotRow).filter(Boolean),
  };
}

function stockInputFromQty(qty) {
  return qty == null ? "" : String(qty);
}

export function snapshotIdsFromEvent(event) {
  const { addons, preorders } = eventCatalogSnapshots(event);
  return {
    addonItemIds: addons.map((r) => r.itemId),
    preorderItemIds: preorders.map((r) => r.itemId),
    addonPriceOverrides: Object.fromEntries(
      addons.map((r) => [r.itemId, r.unitPrice])
    ),
    preorderPriceOverrides: Object.fromEntries(
      preorders.map((r) => [r.itemId, r.unitPrice])
    ),
    addonStockQty: Object.fromEntries(
      addons.map((r) => [r.itemId, stockInputFromQty(r.qty)])
    ),
    preorderStockQty: Object.fromEntries(
      preorders.map((r) => [r.itemId, stockInputFromQty(r.qty)])
    ),
  };
}

/** Empty → unlimited. Filled must be an integer 1–100000. */
export function parseEventStockInput(raw) {
  if (raw == null) return { ok: true, unlimited: true };
  const s = String(raw).trim();
  if (s === "") return { ok: true, unlimited: true };
  if (!/^\d+$/.test(s)) return { ok: false };
  const n = parseInt(s, 10);
  if (
    !Number.isFinite(n) ||
    n < CATALOG_EVENT_STOCK_MIN ||
    n > CATALOG_EVENT_STOCK_MAX
  ) {
    return { ok: false };
  }
  return { ok: true, unlimited: false, qty: n };
}

function asIdList(value) {
  if (!Array.isArray(value)) return [];
  return [...new Set(value.map((id) => String(id || "").trim()).filter(Boolean))];
}

function attachLineForItem(id, priceOverrides, stockMap, catalogById) {
  const line = { itemId: id };
  const parsed = parseEventStockInput(stockMap?.[id]);
  if (parsed.ok && !parsed.unlimited) {
    line.qty = parsed.qty;
  }
  const raw = priceOverrides?.[id];
  if (raw != null && raw !== "") {
    const n = Number(raw);
    if (Number.isFinite(n) && n >= 0) {
      const catalogPrice = Number(catalogById.get(id)?.unitPrice);
      if (!(Number.isFinite(catalogPrice) && n === catalogPrice)) {
        line.unitPrice = n;
      }
    }
  }
  return line;
}

function buildAttachLines(ids, priceOverrides, stockMap, catalogById) {
  return ids.map((id) =>
    attachLineForItem(id, priceOverrides, stockMap, catalogById)
  );
}

export function validateEventCatalogAttach(
  {
    addonItemIds,
    preorderItemIds,
    addonStockQty,
    preorderStockQty,
  },
  eventItem,
  t
) {
  const snaps = eventCatalogSnapshots(eventItem);
  const byId = new Map(
    [...snaps.addons, ...snaps.preorders].map((row) => [row.itemId, row])
  );
  const checks = [
    [asIdList(addonItemIds), addonStockQty],
    [asIdList(preorderItemIds), preorderStockQty],
  ];
  for (const [ids, stockMap] of checks) {
    for (const id of ids) {
      const parsed = parseEventStockInput(stockMap?.[id]);
      if (!parsed.ok) {
        return t("CATALOG_EVENT_STOCK_INVALID");
      }
      const snap = byId.get(id);
      const sold = Number(snap?.qtySold) || 0;
      if (!parsed.unlimited && sold > 0 && parsed.qty < sold) {
        return t("CATALOG_EVENT_STOCK_BELOW_SOLD", {
          name: snap?.name || id,
          sold,
        });
      }
    }
  }
  return null;
}

/**
 * Event create/update catalog attach. When clubid is set, send addons/preorders
 * object arrays (itemId + optional qty/unitPrice). Empty arrays clear.
 * Omit this helper’s result when catalog is not being edited.
 */
export function buildEventCatalogPayload({
  clubId,
  addonItemIds,
  preorderItemIds,
  addonPriceOverrides,
  preorderPriceOverrides,
  addonStockQty,
  preorderStockQty,
  catalogById,
}) {
  const cid = clubId != null && String(clubId).trim() !== "" ? String(clubId).trim() : "";
  if (!cid) return {};
  const addonIds = asIdList(addonItemIds);
  const preorderIds = asIdList(preorderItemIds);
  const map = catalogById instanceof Map ? catalogById : new Map();
  return {
    addons: buildAttachLines(
      addonIds,
      addonPriceOverrides,
      addonStockQty,
      map
    ),
    preorders: buildAttachLines(
      preorderIds,
      preorderPriceOverrides,
      preorderStockQty,
      map
    ),
  };
}

export function formatCatalogMoney(amount, currency = "INR") {
  const n = Number(amount);
  if (!Number.isFinite(n)) return "—";
  try {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: currency || "INR",
      maximumFractionDigits: n % 1 === 0 ? 0 : 2,
    }).format(n);
  } catch {
    return `₹${n.toLocaleString("en-IN")}`;
  }
}

export function fileToDataImageUrl(file) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("No file"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Could not read image"));
    reader.readAsDataURL(file);
  });
}

export function validateCatalogItemForm(form, t) {
  const errors = {};
  const name = String(form.name ?? "").trim();
  if (!name || name.length > CATALOG_NAME_MAX) {
    errors.name = t("CATALOG_NAME_INVALID");
  }
  const description = String(form.description ?? "");
  if (description.length > CATALOG_DESCRIPTION_MAX) {
    errors.description = t("CATALOG_DESCRIPTION_INVALID");
  }
  const unitPrice = Number(form.unitPrice);
  if (!Number.isFinite(unitPrice) || unitPrice < 0) {
    errors.unitPrice = t("CATALOG_PRICE_INVALID");
  }
  const sortOrder = form.sortOrder === "" || form.sortOrder == null ? 0 : Number(form.sortOrder);
  if (!Number.isFinite(sortOrder) || sortOrder < 0 || sortOrder > CATALOG_SORT_MAX) {
    errors.sortOrder = t("CATALOG_SORT_INVALID");
  }
  const kind = String(form.kind ?? "").toUpperCase();
  if (kind !== CATALOG_KIND_ADDON && kind !== CATALOG_KIND_PREORDER) {
    errors.kind = t("CATALOG_KIND_INVALID");
  }
  return errors;
}

export function numMoney(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

export function orderAddonLines(order) {
  const lines = Array.isArray(order?.addonLines)
    ? order.addonLines
    : Array.isArray(order?.addons)
      ? order.addons
      : [];
  return lines
    .map((line) => ({
      itemId: catalogItemId(line),
      kind: String(line?.kind ?? CATALOG_KIND_ADDON).toUpperCase(),
      name: String(line?.name ?? "").trim() || "—",
      qty: Number(line?.qty ?? 1) || 1,
      unitPrice: numMoney(line?.unitPrice),
      lineTotal: numMoney(line?.lineTotal ?? Number(line?.qty ?? 1) * Number(line?.unitPrice ?? 0)),
    }))
    .filter((line) => line.name);
}

export function orderAddonTotal(order) {
  if (order?.addonTotal != null && order.addonTotal !== "") {
    return numMoney(order.addonTotal);
  }
  return orderAddonLines(order).reduce((sum, line) => sum + line.lineTotal, 0);
}

export function tableBookingCatalogSnapshot(booking) {
  const addons = Array.isArray(booking?.addons) ? booking.addons : [];
  const preorders = Array.isArray(booking?.preorders) ? booking.preorders : [];
  const addonLines = addons.map((line) => ({
    itemId: catalogItemId(line),
    name: String(line?.name ?? "").trim() || "—",
    qty: Number(line?.qty ?? 1) || 1,
    unitPrice: numMoney(line?.unitPrice),
    lineTotal: numMoney(line?.lineTotal ?? Number(line?.qty ?? 1) * Number(line?.unitPrice ?? 0)),
  }));
  const preorderLines = preorders.map((line) => ({
    itemId: catalogItemId(line),
    name: String(line?.name ?? "").trim() || "—",
    qty: Number(line?.qty ?? 1) || 1,
    unitPrice: numMoney(line?.unitPrice),
    lineTotal: numMoney(line?.lineTotal ?? Number(line?.qty ?? 1) * Number(line?.unitPrice ?? 0)),
  }));
  const minimumSpend = numMoney(booking?.minimumSpend ?? booking?.minimumspend);
  const depositAmount = numMoney(booking?.depositAmount ?? booking?.depositamount);
  const addonTotal =
    booking?.addonTotal != null && booking.addonTotal !== ""
      ? numMoney(booking.addonTotal)
      : addonLines.reduce((s, l) => s + l.lineTotal, 0);
  const preorderTotal =
    booking?.preorderTotal != null && booking.preorderTotal !== ""
      ? numMoney(booking.preorderTotal)
      : preorderLines.reduce((s, l) => s + l.lineTotal, 0);
  const preorderOverage = numMoney(
    booking?.preorderOverage ?? booking?.preorder_overage ?? booking?.preorderExtra
  );
  const venueDueAmount =
    booking?.venueDueAmount != null && booking.venueDueAmount !== ""
      ? numMoney(booking.venueDueAmount)
      : Math.max(0, minimumSpend - depositAmount);
  return {
    addonLines,
    preorderLines,
    addonTotal,
    preorderTotal,
    preorderOverage,
    venueDueAmount,
    minimumSpend,
    depositAmount,
    payNowAmount: numMoney(booking?.payNowAmount ?? booking?.paynowamount),
    currency: String(booking?.currency ?? "INR") || "INR",
  };
}

export function bookingMatchesEvent(booking, eventId) {
  if (!eventId) return true;
  const id = String(eventId);
  const raw =
    booking?.eventId ??
    booking?.eventid ??
    booking?.event?._id ??
    booking?.event?.id ??
    "";
  if (!raw) return true;
  return String(raw) === id;
}

/**
 * Sum snapshot money on loaded bookings/report rows. Prefer explicit summary
 * fields when the report already includes them; never live catalog prices.
 */
export function pickCatalogReportTotals(data) {
  const summary = data?.summary && typeof data.summary === "object" ? data.summary : {};
  const overall = summary.overall && typeof summary.overall === "object" ? summary.overall : {};
  const tb =
    data?.tableBookings?.summary ??
    data?.table_bookings?.summary ??
    {};

  const ticketFromSummary = overall.addonTotal ?? overall.ticketAddonTotal ?? overall.addonsTotal;
  const tableAddonFromSummary = tb.addonTotal ?? tb.tableAddonTotal;
  const preorderFromSummary = tb.preorderTotal;
  const extraFromSummary = tb.preorderOverage ?? tb.preorderExtra ?? tb.preorderExtraTotal;

  const tableLines = [
    ...(Array.isArray(data?.tableBookingLines) ? data.tableBookingLines : []),
    ...(Array.isArray(data?.table_booking_lines) ? data.table_booking_lines : []),
  ];
  const ticketRows = [
    ...(Array.isArray(data?.bookings) ? data.bookings : []),
    ...(Array.isArray(data?.items) ? data.items : []),
  ];

  const summedTable = tableLines.reduce(
    (acc, row) => {
      const snap = tableBookingCatalogSnapshot(row);
      acc.tableAddon += snap.addonTotal;
      acc.preorder += snap.preorderTotal;
      acc.preorderExtra += snap.preorderOverage;
      return acc;
    },
    { tableAddon: 0, preorder: 0, preorderExtra: 0 }
  );
  const summedTickets = ticketRows.reduce((sum, row) => sum + orderAddonTotal(row), 0);

  const byEvent = Array.isArray(data?.byEvent) ? data.byEvent : [];
  const byEventTickets = byEvent.reduce(
    (sum, row) =>
      sum +
      numMoney(
        row?.addonTotal ?? row?.ticketAddonTotal ?? row?.addonsTotal
      ),
    0
  );
  const tbByEvent = Array.isArray(data?.tableBookings?.byEvent)
    ? data.tableBookings.byEvent
    : [];
  const byEventTable = tbByEvent.reduce(
    (acc, row) => {
      acc.tableAddon += numMoney(row?.addonTotal);
      acc.preorder += numMoney(row?.preorderTotal);
      acc.preorderExtra += numMoney(row?.preorderOverage ?? row?.preorderExtra);
      return acc;
    },
    { tableAddon: 0, preorder: 0, preorderExtra: 0 }
  );

  return {
    ticketAddon:
      ticketFromSummary != null && ticketFromSummary !== ""
        ? numMoney(ticketFromSummary)
        : summedTickets || byEventTickets,
    tableAddon:
      tableAddonFromSummary != null && tableAddonFromSummary !== ""
        ? numMoney(tableAddonFromSummary)
        : summedTable.tableAddon || byEventTable.tableAddon,
    preorder:
      preorderFromSummary != null && preorderFromSummary !== ""
        ? numMoney(preorderFromSummary)
        : summedTable.preorder || byEventTable.preorder,
    preorderExtra:
      extraFromSummary != null && extraFromSummary !== ""
        ? numMoney(extraFromSummary)
        : summedTable.preorderExtra || byEventTable.preorderExtra,
  };
}
